import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Upload, X, FileText, User, Settings2 } from "lucide-react";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { apiRequest, API_URL, getToken } from "../lib/utils";
import { toast } from "../components/ToastProvider";
import { Plus, Trash2 } from "lucide-react";

const PERIODICIDADES_PADRAO = [
  { value: "nunca", label: "Nunca (não vence)" },
  { value: "mensal", label: "Mensal" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
  { value: "bianual", label: "Bianual" },
];

export const DocumentoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Estados para domínios customizados
  const [periodicidades, setPeriodicidades] = useState(PERIODICIDADES_PADRAO);
  const [periodicidadeModalOpen, setPeriodicidadeModalOpen] = useState(false);
  const [novaPeriodicidade, setNovaPeriodicidade] = useState("");
  const [loadingPeriodicidade, setLoadingPeriodicidade] = useState(false);

  const [formData, setFormData] = useState({
    nome_documento: "",
    periodicidade: "anual",
    data_ultima_execucao: "",
    data_proximo_vencimento: "",
    responsavel: "",
    responsavel_id: "",
    responsavel_email: "",
    observacoes: "",
    arquivo_url: "",
    arquivo_nome: "",
  });

  const [usuarios, setUsuarios] = useState([]);

  useEffect(() => {
    fetchUsuarios();
    fetchPeriodicidades();
    if (isEditing) {
      fetchDocumento();
    }
  }, [id]);

  const fetchPeriodicidades = async () => {
    try {
      const data = await apiRequest("/dominios/periodicidade");
      if (data.opcoes && data.opcoes.length > 0) {
        setPeriodicidades(data.opcoes);
      }
    } catch (error) {
      console.error("Erro ao carregar periodicidades:", error);
      // Mantém as opções padrão em caso de erro
    }
  };

  const adicionarPeriodicidade = async () => {
    if (!novaPeriodicidade.trim()) {
      toast.error("Digite um nome para a periodicidade");
      return;
    }
    setLoadingPeriodicidade(true);
    try {
      const opcao = await apiRequest("/dominios/periodicidade", {
        method: "POST",
        body: JSON.stringify({ nome: novaPeriodicidade.trim() })
      });
      setPeriodicidades([...periodicidades, opcao]);
      setNovaPeriodicidade("");
      toast.success("Periodicidade adicionada!");
    } catch (error) {
      toast.error(error.message || "Erro ao adicionar periodicidade");
    } finally {
      setLoadingPeriodicidade(false);
    }
  };

  const removerPeriodicidade = async (opcao) => {
    if (!window.confirm("Tem certeza que deseja remover esta periodicidade?")) return;
    const deleteId = opcao.id || opcao.value;
    try {
      await apiRequest(`/dominios/periodicidade/${deleteId}`, { method: "DELETE" });
      setPeriodicidades(periodicidades.filter(p => p.value !== opcao.value));
      toast.success("Periodicidade removida!");
    } catch (error) {
      toast.error("Erro ao remover periodicidade");
    }
  };

  const fetchUsuarios = async () => {
    try {
      const data = await apiRequest("/usuarios/responsaveis");
      setUsuarios(data);
      // Se só tem 1 usuário e não está editando, definir como responsável padrão
      if (data.length === 1 && !isEditing) {
        setFormData(prev => ({
          ...prev,
          responsavel: data[0].nome,
          responsavel_id: data[0].id,
          responsavel_email: data[0].email
        }));
      }
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  };

  const fetchDocumento = async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/documentos/${id}`);
      setFormData({
        nome_documento: data.nome_documento || "",
        periodicidade: data.periodicidade || "anual",
        data_ultima_execucao: data.data_ultima_execucao || "",
        data_proximo_vencimento: data.data_proximo_vencimento || "",
        responsavel: data.responsavel || "",
        responsavel_id: data.responsavel_id || "",
        responsavel_email: data.responsavel_email || "",
        observacoes: data.observacoes || "",
        arquivo_url: data.arquivo_url || "",
        arquivo_nome: data.arquivo_nome || "",
      });
    } catch (error) {
      toast.error("Erro ao carregar documento");
      navigate("/documentos");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      // Se mudar periodicidade para "nunca", limpar data de vencimento
      if (field === "periodicidade" && value === "nunca") {
        newData.data_proximo_vencimento = "";
      }
      return newData;
    });
  };

  const handleResponsavelChange = (userId) => {
    const usuario = usuarios.find(u => u.id === userId);
    if (usuario) {
      setFormData(prev => ({
        ...prev,
        responsavel: usuario.nome,
        responsavel_id: usuario.id,
        responsavel_email: usuario.email
      }));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamanho (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 10MB.");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) throw new Error("Erro no upload");

      const result = await response.json();
      setFormData((prev) => ({
        ...prev,
        arquivo_url: result.url,
        arquivo_nome: file.name,
      }));
      toast.success("Arquivo enviado com sucesso!");
    } catch (error) {
      toast.error("Erro ao enviar arquivo");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setFormData((prev) => ({
      ...prev,
      arquivo_url: "",
      arquivo_nome: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = { ...formData };
      
      // Se periodicidade for "nunca", limpar data de vencimento
      if (payload.periodicidade === "nunca") {
        payload.data_proximo_vencimento = null;
      }
      
      // Converter campos vazios para null
      if (payload.data_ultima_execucao === "") {
        payload.data_ultima_execucao = null;
      }
      if (payload.data_proximo_vencimento === "") {
        payload.data_proximo_vencimento = null;
      }
      if (payload.responsavel === "") {
        payload.responsavel = null;
      }
      if (payload.observacoes === "") {
        payload.observacoes = null;
      }
      if (payload.arquivo_url === "") {
        payload.arquivo_url = null;
      }
      if (payload.arquivo_nome === "") {
        payload.arquivo_nome = null;
      }

      if (isEditing) {
        await apiRequest(`/documentos/${id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        toast.success("Documento atualizado com sucesso!");
      } else {
        await apiRequest("/documentos", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.success("Documento cadastrado com sucesso!");
      }
      navigate("/documentos");
    } catch (error) {
      toast.error(isEditing ? "Erro ao atualizar documento" : "Erro ao cadastrar documento");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl animate-slide-in" data-testid="documento-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/documentos">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              {isEditing ? "Editar Documento" : "Novo Documento"}
            </h1>
            <p className="text-slate-500 mt-1">
              {isEditing ? "Atualize as informações do documento" : "Cadastre um novo documento ou alvara"}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações do Documento */}
          <Card>
            <CardHeader>
              <CardTitle>Informações do Documento</CardTitle>
              <CardDescription>Dados gerais do documento ou alvara</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="nome">Nome do Documento *</Label>
                  <Input
                    id="nome"
                    value={formData.nome_documento}
                    onChange={(e) => handleChange("nome_documento", e.target.value)}
                    placeholder="Ex: Alvara Sanitario, Licença de Funcionamento"
                    required
                    data-testid="input-nome"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Periodicidade *</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.periodicidade}
                      onValueChange={(value) => handleChange("periodicidade", value)}
                    >
                      <SelectTrigger data-testid="select-periodicidade" className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {periodicidades.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setPeriodicidadeModalOpen(true)}
                      title="Gerenciar Periodicidades"
                    >
                      <Settings2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Responsável
                  </Label>
                  <Select
                    value={formData.responsavel_id || ""}
                    onValueChange={handleResponsavelChange}
                  >
                    <SelectTrigger data-testid="select-responsavel">
                      <SelectValue placeholder="Selecione o responsável" />
                    </SelectTrigger>
                    <SelectContent>
                      {usuarios.map((usuario) => (
                        <SelectItem key={usuario.id} value={usuario.id}>
                          {usuario.nome} ({usuario.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.responsavel && (
                    <p className="text-xs text-slate-500">
                      Email: {formData.responsavel_email}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Data da Última Execucao</Label>
                  <Input
                    type="date"
                    value={formData.data_ultima_execucao}
                    onChange={(e) => handleChange("data_ultima_execucao", e.target.value)}
                    data-testid="input-última-execucao"
                  />
                </div>

                {formData.periodicidade !== "nunca" && (
                  <div className="space-y-2">
                    <Label>Próximo Vencimento *</Label>
                    <Input
                      type="date"
                      value={formData.data_proximo_vencimento}
                      onChange={(e) => handleChange("data_proximo_vencimento", e.target.value)}
                      required
                      data-testid="input-vencimento"
                    />
                  </div>
                )}

                <div className="space-y-2 md:col-span-2">
                  <Label>Observacoes</Label>
                  <Textarea
                    value={formData.observacoes}
                    onChange={(e) => handleChange("observacoes", e.target.value)}
                    placeholder="Observacoes adicionais sobre o documento..."
                    rows={3}
                    data-testid="input-observacoes"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upload de Arquivo */}
          <Card>
            <CardHeader>
              <CardTitle>Arquivo do Documento</CardTitle>
              <CardDescription>Faca upload do documento digitalizado (PDF, imagem)</CardDescription>
            </CardHeader>
            <CardContent>
              {formData.arquivo_url ? (
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <FileText className="w-8 h-8 text-teal-600" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{formData.arquivo_nome}</p>
                    <a 
                      href={formData.arquivo_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-teal-600 hover:underline"
                    >
                      Visualizar arquivo
                    </a>
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="icon"
                    onClick={removeFile}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-teal-400 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    onChange={handleFileUpload}
                  />
                  {uploading ? (
                    <Loader2 className="w-8 h-8 mx-auto text-teal-600 animate-spin" />
                  ) : (
                    <Upload className="w-8 h-8 mx-auto text-slate-400" />
                  )}
                  <p className="mt-2 text-sm text-slate-600">
                    {uploading ? "Enviando..." : "Clique para fazer upload"}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    PDF, DOC, DOCX, PNG, JPG (max. 10MB)
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" asChild>
              <Link to="/documentos">Cancelar</Link>
            </Button>
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={saving} data-testid="submit-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? "Salvar alteracoes" : "Cadastrar documento"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Modal de Gerenciamento de Periodicidades */}
      <Dialog open={periodicidadeModalOpen} onOpenChange={setPeriodicidadeModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-teal-600" />
              Gerenciar Periodicidades
            </DialogTitle>
            <DialogDescription>
              Adicione ou remova opções de periodicidade
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Adicionar nova periodicidade */}
            <div className="flex gap-2">
              <Input
                placeholder="Nome da nova periodicidade"
                value={novaPeriodicidade}
                onChange={(e) => setNovaPeriodicidade(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && adicionarPeriodicidade()}
                disabled={loadingPeriodicidade}
              />
              <Button
                type="button"
                onClick={adicionarPeriodicidade}
                disabled={loadingPeriodicidade || !novaPeriodicidade.trim()}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {loadingPeriodicidade ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Lista de periodicidades existentes */}
            <div className="max-h-60 overflow-y-auto space-y-2">
              {periodicidades.map((p) => (
                <div
                  key={p.value}
                  className="flex items-center justify-between p-2 rounded-lg border border-slate-200 bg-slate-50"
                >
                  <span className="text-sm">{p.label}</span>
                  {!p.padrao && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removerPeriodicidade(p)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPeriodicidadeModalOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default DocumentoForm;
